import { HStack } from "@/components/layout/HStack";
import { VStack } from "@/components/layout/VStack";
import { Button } from "@/components/ui/Button/Button";
import { Label } from "@/components/ui/Label/Label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select/Select";
import {
  SheetBody,
  SheetClose,
  SheetFooter,
} from "@/components/ui/Sheet/Sheet";
import { Skeleton } from "@/components/ui/Skeleton/Skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/Tooltip/Tooltip";
import { zodResolver } from "@hookform/resolvers/zod";
import { OctagonX } from "lucide-react";
import { Controller, useForm, type SubmitHandler } from "react-hook-form";
import { z } from "zod";
import { useSignUp } from "../../hooks/signUp/useSignUp";

const schema = z.object({
  explorationId: z
    .string("Exploration is required")
    .min(1, "Exploration is required"),
});

export type EditSingletonSignUpFormFields = z.infer<typeof schema>;

const EditSingletonSignUpForm = ({
  data,
  loading,
  onSuccess,
}: {
  data: {
    explorations: any[];
    enrollment: any;
    sessionId: string;
  };
  loading: boolean;
  onSuccess: ({
    enrollment,
    preferences,
  }: {
    enrollment?: any;
    preferences?: any;
  }) => void;
}) => {
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<EditSingletonSignUpFormFields>({
    resolver: zodResolver(schema),
    defaultValues: {
      explorationId: data.enrollment.exploration,
    },
  });

  const { updateSignUp } = useSignUp();

  const onSubmit: SubmitHandler<EditSingletonSignUpFormFields> = async (
    formData,
  ) => {
    const result = await updateSignUp(data.sessionId, formData.explorationId);
    onSuccess({ enrollment: result });
  };

  return (
    <>
      <SheetBody>
        <form id="singleton-sign-up-form" onSubmit={handleSubmit(onSubmit)}>
          <VStack>
            <VStack gap="2px">
              <HStack gap="0.25rem">
                <Label>Exploration</Label>
                {errors.explorationId && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <OctagonX
                        style={{
                          color: "var(--danger)",
                          width: "1rem",
                          height: "1rem",
                          cursor: "default",
                        }}
                      />
                    </TooltipTrigger>
                    <TooltipContent>
                      {errors.explorationId.message}
                    </TooltipContent>
                  </Tooltip>
                )}
              </HStack>
              {loading ? (
                <Skeleton style={{ height: "36px", width: "100%" }} />
              ) : (
                <Controller
                  control={control}
                  name="explorationId"
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      onValueChange={(v) => field.onChange(v)}
                    >
                      <SelectTrigger aria-invalid={!!errors.explorationId}>
                        <SelectValue placeholder="Select an exploration" />
                      </SelectTrigger>
                      <SelectContent>
                        {data.explorations.map((e) => (
                          <SelectItem key={e._id} value={e._id}>
                            {e.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              )}
            </VStack>
          </VStack>
        </form>
      </SheetBody>
      <SheetFooter>
        <Button
          type="submit"
          form="singleton-sign-up-form"
          style={{ width: "100%" }}
        >
          Save Changes
        </Button>
        <SheetClose asChild>
          <Button variant="outline" style={{ width: "100%" }}>
            Cancel
          </Button>
        </SheetClose>
      </SheetFooter>
    </>
  );
};

export { EditSingletonSignUpForm };
